/**
 * Shared per-file validation for the `chat-attachment` S3 upload scenario.
 *
 * Single source of truth for the chat-attachment upload gate. Called by:
 * - The server-side scenario handler in `s3-files.manager.ts` (security gate).
 * - The frontend `useFileUpload` hook (pre-upload UX validation).
 *
 * MIME allowlist and per-MIME size caps are embedded in the function rather
 * than exported as a separate constants table — the function itself is the
 * authoritative shape. Rejection reasons are returned verbatim so the same
 * string surfaces in the composer chip as in the server's 400 response.
 *
 * Per-turn count caps are not encoded here — they are a chat-surface UX
 * concern, passed down as configuration into the composer.
 */

import { formatBytes } from "./bytes";

type ChatAttachmentLimits = Readonly<
  Record<string, { readonly maxBytes: number }>
>;

// Per-turn token cost for text formats compounds: an attached file is
// persisted on the user message and re-sent on every subsequent turn until
// staleness redaction kicks in. Redaction currently mutates persisted
// state (so it can't safely cover file uploads — would erase the chat
// history's file tile), so this cap is the only bound on per-turn
// text-upload cost. 100KB ≈ 25K tokens per turn — substantial but
// bounded. Revisit upward once redaction becomes a per-call view
// (see [[project_redaction_as_view]]).
const TEXT_CAP_BYTES = 100_000;

const LIMITS: ChatAttachmentLimits = {
  // Multimodal via Claude image block (also supported by OpenAI / Gemini)
  "image/png": { maxBytes: 5_000_000 },
  "image/jpeg": { maxBytes: 5_000_000 },
  "image/gif": { maxBytes: 5_000_000 },
  "image/webp": { maxBytes: 5_000_000 },
  // Multimodal via Claude document block (Anthropic + Gemini; not OpenAI)
  "application/pdf": { maxBytes: 32_000_000 },
  // Text formats — inlined as a text content block on every LLM call (see
  // `file-inlining.ts`). Bytes become tokens ~1:1 (~4 chars/token); see
  // TEXT_CAP_BYTES rationale above for the per-turn budgeting.
  "text/plain": { maxBytes: TEXT_CAP_BYTES },
  "text/csv": { maxBytes: TEXT_CAP_BYTES },
  "text/tab-separated-values": { maxBytes: TEXT_CAP_BYTES },
  "text/markdown": { maxBytes: TEXT_CAP_BYTES },
  "text/html": { maxBytes: TEXT_CAP_BYTES },
  "text/css": { maxBytes: TEXT_CAP_BYTES },
  "application/json": { maxBytes: TEXT_CAP_BYTES },
  // SVG is `image/*` by MIME, but Anthropic's image content block rejects
  // it (it's XML, not raster). The visual builder benefits from reading
  // the markup directly though — paths, viewBox, brand colors — so we
  // route SVG through the same text-inlining path as the formats above
  // rather than through the image content block.
  "image/svg+xml": { maxBytes: TEXT_CAP_BYTES },
};

export type ChatAttachmentInput = {
  filename?: string;
  mediaType?: string;
  size?: number;
};

export type ChatAttachmentCheck =
  | { accept: true }
  | {
      accept: false;
      code: "incomplete" | "unsupported_type" | "too_large";
      reason: string;
    };

export function checkChatAttachment(
  input: ChatAttachmentInput
): ChatAttachmentCheck {
  if (!input.filename || !input.mediaType || input.size == null) {
    return {
      accept: false,
      code: "incomplete",
      reason: "filename, mediaType, and size are required",
    };
  }
  const limit = LIMITS[input.mediaType];
  if (!limit) {
    return {
      accept: false,
      code: "unsupported_type",
      reason: `${input.mediaType} is not supported`,
    };
  }
  if (input.size > limit.maxBytes) {
    return {
      accept: false,
      code: "too_large",
      reason: `File exceeds max size (${formatBytes(limit.maxBytes)})`,
    };
  }
  return { accept: true };
}

/**
 * Allowed MIME types as a frozen array. Use to populate the `accept` attribute
 * on file pickers so the UI gate stays in sync with `checkChatAttachment`.
 */
export const listAllowedChatAttachmentMimes = (): readonly string[] =>
  Object.freeze(Object.keys(LIMITS));

/** Image MIME types accepted by the chat attachment gate. */
export const listAllowedChatImageMimes = (): readonly string[] =>
  Object.freeze(
    listAllowedChatAttachmentMimes().filter((mime) => mime.startsWith("image/"))
  );
